/**
 * Notification Trigger Service
 *
 * Service che ascolta gli eventi domain (gli stessi del WorkflowTriggerService)
 * e crea le notifiche in-app corrispondenti.
 *
 * Approccio ibrido:
 * - INSERT diretto per notifiche in-app → UI reattiva via realtime subscription
 * - Edge function async (fire-and-forget) per email/push
 *
 * Usage:
 * ```typescript
 * import { notificationTriggerService } from './notificationTriggerService';
 *
 * // All'avvio dell'app (in main.tsx)
 * notificationTriggerService.init();
 * ```
 */
import { eventBus } from './eventBus';
import { supabase } from '../supabase';
import { buildBulkNotificationMessage } from '../utils/sectionBulkAssign';
// Event types are inferred from eventBus.onEvent calls

interface NotificationData {
  title: string;
  message: string;
  link?: string;
  entityType: string;
  entityId: string;
  senderId?: string;
  senderName?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  metadata?: Record<string, unknown>;
}

interface InsertNotificationParams {
  eventType: string;
  organizationId: string;
  recipients: string[];
  data: NotificationData;
  excludeUserId?: string;
}

class NotificationTriggerService {
  private initialized = false;
  private currentUserId: string | null = null;
  private currentUserEmail: string | null = null;
  private currentUserName: string | null = null;
  private currentOrganizationId: string | null = null;
  private recipientRolesCache: Map<string, string[]> = new Map();
  private activeTaskDrawerTaskId: string | null = null;
  private clientCommentDebounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Initialize all event listeners
   * DEVE essere chiamato UNA VOLTA all'avvio dell'applicazione
   */
  async init(): Promise<void> {
    if (this.initialized) {
      console.warn('[NotificationTriggerService] ⚠️ Already initialized');
      return;
    }

    console.log('[NotificationTriggerService] 🔔 Initializing notification triggers...');

    // Cache current user info
    await this.refreshCurrentUser();

    // Register all event handlers
    this.registerLeadEvents();
    this.registerDealEvents();
    this.registerClientEvents();
    this.registerProjectEvents();
    this.registerTaskEvents();
    this.registerPartnerTaskEvents();

    this.initialized = true;

    console.log('[NotificationTriggerService] ✅ Initialized');
  }

  /**
   * Refresh cached user info (call after login)
   */
  async refreshCurrentUser(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        this.currentUserId = user.id;
        this.currentUserEmail = user.email || null;

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('full_name, organization_id')
          .eq('id', user.id)
          .single();

        this.currentUserName = profile?.full_name || null;
        this.currentOrganizationId = profile?.organization_id || null;
      }
    } catch (error) {
      console.error('[NotificationTriggerService] Error refreshing user:', error);
    }
  }

  /**
   * Cleanup all listeners
   */
  destroy(): void {
    // Note: Non possiamo rimuovere solo i nostri listener senza rimuovere anche
    // quelli di WorkflowTriggerService. Questo metodo è principalmente per testing.
    this.initialized = false;
    console.log('[NotificationTriggerService] 🗑️ Destroyed');
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Imposta/resetta il task attualmente aperto nel TaskDrawer.
   * Usato per il debounce delle notifiche messaggi cliente.
   */
  setActiveTaskDrawerTask(taskId: string | null): void {
    this.activeTaskDrawerTaskId = taskId;
  }

  // ============================================================
  // LEAD EVENT HANDLERS
  // ============================================================

  private registerLeadEvents() {
    // Lead/Cliente assegnato (creazione con assegnatario o cambio di assigned_to):
    // la notifica la genera il database, da qualunque percorso (CRM, operazioni di
    // gruppo, flussi, MCP, core, import, round-robin), con una sola notifica per
    // operazione. Vedi supabase/migrations/20260928g_notifica_assegnazione_lead_clienti.sql.
    // Qui non si notifica piu' nulla per 'lead:created' e 'lead:assigned', altrimenti
    // l'assegnatario riceverebbe tutto due volte. Gli eventi restano emessi per flussi
    // e statistiche.

    // Lead State Changed → Notifica in base ai ruoli configurati
    eventBus.onEvent('lead:state_changed', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 lead:state_changed', { leadId: payload.leadId });

        const { data: lead } = await supabase
          .from('clients')
          .select('organization_id, assigned_to, created_by, sales_rep_id')
          .eq('id', payload.leadId)
          .single();

        if (!lead?.organization_id) return;

        const { data: newState } = await supabase
          .from('lead_pipeline_stages')
          .select('name')
          .eq('id', payload.toStateId)
          .single();

        const roles = await this.getRecipientRoles('lead_stage_changed');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: lead.assigned_to,
          creatorId: lead.created_by,
          salesRepId: lead.sales_rep_id,
          entityType: 'lead',
          entityId: payload.leadId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        await this.insertNotifications({
          eventType: 'lead_stage_changed',
          organizationId: lead.organization_id,
          recipients,
          data: {
            title: 'Lead cambiato stato',
            message: `${payload.businessName || 'Lead'} è passato a: ${newState?.name || 'nuovo stato'}`,
            entityType: 'lead',
            entityId: payload.leadId,
            link: `/leads/${payload.leadId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              from_state_id: payload.fromStateId,
              to_state_id: payload.toStateId,
              pipeline_id: payload.pipelineId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in lead:state_changed:', error);
      }
    });

    // Lead Converted → Notifica in base ai ruoli configurati
    eventBus.onEvent('lead:converted_to_client', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 lead:converted_to_client', { leadId: payload.leadId });

        const { data: client } = await supabase
          .from('clients')
          .select('organization_id, assigned_to, created_by, sales_rep_id')
          .eq('id', payload.clientId)
          .single();

        if (!client?.organization_id) return;

        const roles = await this.getRecipientRoles('lead_converted');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: client.assigned_to,
          creatorId: client.created_by,
          salesRepId: client.sales_rep_id,
          entityType: 'lead',
          entityId: payload.leadId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        await this.insertNotifications({
          eventType: 'lead_converted',
          organizationId: client.organization_id,
          recipients,
          data: {
            title: 'Lead convertito in cliente',
            message: `${payload.businessName || 'Lead'} è stato convertito in cliente`,
            entityType: 'client',
            entityId: payload.clientId,
            link: `/clients/${payload.clientId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              original_lead_id: payload.leadId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in lead:converted_to_client:', error);
      }
    });

    // Lead Note Created → Notifica in base ai ruoli configurati
    eventBus.onEvent('lead:note_created', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 lead:note_created', { leadId: payload.leadId, noteId: payload.noteId });

        const { data: lead } = await supabase
          .from('clients')
          .select('organization_id, business_name, assigned_to, created_by, sales_rep_id')
          .eq('id', payload.leadId)
          .single();

        if (!lead?.organization_id) return;

        const roles = await this.getRecipientRoles('note_created');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: lead.assigned_to,
          creatorId: lead.created_by,
          salesRepId: lead.sales_rep_id,
          entityType: 'lead',
          entityId: payload.leadId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const notePreview = payload.noteContent
          ? payload.noteContent.substring(0, 100) + (payload.noteContent.length > 100 ? '...' : '')
          : '';

        await this.insertNotifications({
          eventType: 'note_created',
          organizationId: lead.organization_id,
          recipients,
          data: {
            title: 'Nuova nota su lead',
            message: `${this.currentUserName || 'Qualcuno'} ha aggiunto una nota su "${lead.business_name || payload.businessName || 'Lead'}"${notePreview ? `: ${notePreview}` : ''}`,
            entityType: 'lead',
            entityId: payload.leadId,
            link: `/leads/${payload.leadId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              note_id: payload.noteId,
              pipeline_id: payload.pipelineId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in lead:note_created:', error);
      }
    });
  }

  // ============================================================
  // DEAL EVENT HANDLERS
  // ============================================================

  private registerDealEvents() {
    // Deal Created → Notifica in base ai ruoli configurati
    eventBus.onEvent('deal:created', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 deal:created', { dealId: payload.dealId });

        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id, assigned_to, title')
          .eq('id', payload.dealId)
          .single();

        if (!deal?.organization_id) return;

        const roles = await this.getRecipientRoles('deal_created');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: deal.assigned_to,
          entityType: 'deal',
          entityId: payload.dealId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        await this.insertNotifications({
          eventType: 'deal_created',
          organizationId: deal.organization_id,
          recipients,
          data: {
            title: 'Nuova trattativa creata',
            message: `${this.currentUserName || 'Qualcuno'} ha creato una nuova trattativa: ${deal.title || payload.title || 'Trattativa'}`,
            entityType: 'deal',
            entityId: payload.dealId,
            link: `/deals/${payload.dealId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              client_id: payload.clientId,
              lead_id: payload.leadId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in deal:created:', error);
      }
    });

    // Deal Note Created → Notifica in base ai ruoli configurati
    eventBus.onEvent('deal:note_created', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 deal:note_created', { dealId: payload.dealId, noteId: payload.noteId });

        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id, title, assigned_to')
          .eq('id', payload.dealId)
          .single();

        if (!deal?.organization_id) return;

        const roles = await this.getRecipientRoles('note_created');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: deal.assigned_to,
          entityType: 'deal',
          entityId: payload.dealId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const notePreview = payload.noteContent
          ? payload.noteContent.substring(0, 100) + (payload.noteContent.length > 100 ? '...' : '')
          : '';

        await this.insertNotifications({
          eventType: 'note_created',
          organizationId: deal.organization_id,
          recipients,
          data: {
            title: 'Nuova nota su trattativa',
            message: `${this.currentUserName || 'Qualcuno'} ha aggiunto una nota su "${deal.title || payload.title || 'Trattativa'}"${notePreview ? `: ${notePreview}` : ''}`,
            entityType: 'deal',
            entityId: payload.dealId,
            link: `/deals/${payload.dealId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              note_id: payload.noteId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in deal:note_created:', error);
      }
    });

    // Deal Assigned → Notifica in base ai ruoli configurati
    eventBus.onEvent('deal:assigned', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 deal:assigned', { dealId: payload.dealId });

        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id, title')
          .eq('id', payload.dealId)
          .single();

        if (!deal?.organization_id) return;

        const roles = await this.getRecipientRoles('deal_assigned');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: payload.toUserId,
          entityType: 'deal',
          entityId: payload.dealId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        await this.insertNotifications({
          eventType: 'deal_assigned',
          organizationId: deal.organization_id,
          recipients,
          data: {
            title: 'Trattativa assegnata',
            message: `${this.currentUserName || 'Qualcuno'} ti ha assegnato la trattativa: ${deal.title || payload.title || 'Trattativa'}`,
            entityType: 'deal',
            entityId: payload.dealId,
            link: `/deals/${payload.dealId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in deal:assigned:', error);
      }
    });

    // Deal State Changed → Notifica in base ai ruoli configurati
    eventBus.onEvent('deal:state_changed', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 deal:state_changed', { dealId: payload.dealId });

        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id, assigned_to')
          .eq('id', payload.dealId)
          .single();

        if (!deal?.organization_id) return;

        const roles = await this.getRecipientRoles('deal_status_changed');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: deal.assigned_to,
          entityType: 'deal',
          entityId: payload.dealId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const statusLabels: Record<string, string> = {
          draft: 'Bozza',
          pending: 'In attesa',
          sent: 'Inviata',
          accepted: 'Accettata',
          rejected: 'Rifiutata',
          signed: 'Firmata',
        };

        await this.insertNotifications({
          eventType: 'deal_status_changed',
          organizationId: deal.organization_id,
          recipients,
          data: {
            title: 'Stato trattativa cambiato',
            message: `${payload.title || 'Trattativa'} è passata a: ${statusLabels[payload.toStatus] || payload.toStatus}`,
            entityType: 'deal',
            entityId: payload.dealId,
            link: `/deals/${payload.dealId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: payload.toStatus === 'signed' ? 'high' : 'normal',
            metadata: {
              from_status: payload.fromStatus,
              to_status: payload.toStatus,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in deal:state_changed:', error);
      }
    });

    // Deal Signed → Notifica in base ai ruoli configurati con alta priorità
    eventBus.onEvent('deal:signed', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 deal:signed', { dealId: payload.dealId });

        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id, assigned_to')
          .eq('id', payload.dealId)
          .single();

        if (!deal?.organization_id) return;

        const roles = await this.getRecipientRoles('deal_won');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: deal.assigned_to,
          entityType: 'deal',
          entityId: payload.dealId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const formattedValue = payload.totalValue
          ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(payload.totalValue)
          : null;

        await this.insertNotifications({
          eventType: 'deal_won',
          organizationId: deal.organization_id,
          recipients,
          data: {
            title: '🎉 Trattativa firmata!',
            message: `${payload.title || 'Trattativa'} è stata firmata${formattedValue ? ` per ${formattedValue}` : ''}`,
            entityType: 'deal',
            entityId: payload.dealId,
            link: `/deals/${payload.dealId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'high',
            metadata: {
              total_value: payload.totalValue,
              signed_at: payload.signedAt,
              client_id: payload.clientId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in deal:signed:', error);
      }
    });
  }

  // ============================================================
  // CLIENT EVENT HANDLERS
  // ============================================================

  private registerClientEvents() {
    // Client State Changed → Notifica in base ai ruoli configurati
    eventBus.onEvent('client:state_changed', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 client:state_changed', { clientId: payload.clientId });

        const { data: client } = await supabase
          .from('clients')
          .select('organization_id, stato_cliente, assigned_to, created_by')
          .eq('id', payload.clientId)
          .single();

        if (!client?.organization_id) return;
        if (await this.isLeadState(client.stato_cliente)) return;

        const { data: newState } = await supabase
          .from('custom_states')
          .select('name')
          .eq('id', payload.toStateId)
          .single();

        const roles = await this.getRecipientRoles('client_updated');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: client.assigned_to,
          creatorId: client.created_by,
          entityType: 'client',
          entityId: payload.clientId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        await this.insertNotifications({
          eventType: 'client_updated',
          organizationId: client.organization_id,
          recipients,
          data: {
            title: 'Stato cliente aggiornato',
            message: `${payload.businessName || 'Cliente'} è passato a: ${newState?.name || 'nuovo stato'}`,
            entityType: 'client',
            entityId: payload.clientId,
            link: `/clients/${payload.clientId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              from_state_id: payload.fromStateId,
              to_state_id: payload.toStateId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in client:state_changed:', error);
      }
    });

    // Client Note Created → Notifica in base ai ruoli configurati
    eventBus.onEvent('client:note_created', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 client:note_created', { clientId: payload.clientId, noteId: payload.noteId });

        const { data: client } = await supabase
          .from('clients')
          .select('organization_id, business_name, stato_cliente, assigned_to, created_by')
          .eq('id', payload.clientId)
          .single();

        if (!client?.organization_id) return;
        if (await this.isLeadState(client.stato_cliente)) return;

        const roles = await this.getRecipientRoles('note_created');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: client.assigned_to,
          creatorId: client.created_by,
          entityType: 'client',
          entityId: payload.clientId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const notePreview = payload.noteContent
          ? payload.noteContent.substring(0, 100) + (payload.noteContent.length > 100 ? '...' : '')
          : '';

        await this.insertNotifications({
          eventType: 'note_created',
          organizationId: client.organization_id,
          recipients,
          data: {
            title: 'Nuova nota su cliente',
            message: `${this.currentUserName || 'Qualcuno'} ha aggiunto una nota su "${client.business_name || payload.businessName || 'Cliente'}"${notePreview ? `: ${notePreview}` : ''}`,
            entityType: 'client',
            entityId: payload.clientId,
            link: `/clients/${payload.clientId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              note_id: payload.noteId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in client:note_created:', error);
      }
    });
  }

  // ============================================================
  // PROJECT EVENT HANDLERS
  // ============================================================

  private registerProjectEvents() {
    // Project Assigned → Notifica in base ai ruoli configurati
    eventBus.onEvent('project:assigned', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 project:assigned', { projectId: payload.projectId });

        const { data: project } = await supabase
          .from('projects')
          .select('organization_id, project_manager_id')
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        const roles = await this.getRecipientRoles('project_assigned');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          assigneeId: payload.userId,
          projectId: payload.projectId,
          projectManagerId: project.project_manager_id,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        await this.insertNotifications({
          eventType: 'project_assigned',
          organizationId: project.organization_id,
          recipients,
          data: {
            title: 'Assegnato a progetto',
            message: `${this.currentUserName || 'Qualcuno'} ti ha assegnato al progetto: ${payload.projectName}`,
            entityType: 'project',
            entityId: payload.projectId,
            link: `/projects/${payload.projectId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in project:assigned:', error);
      }
    });

    // Project Status Changed → Notifica in base ai ruoli configurati
    eventBus.onEvent('project:status_changed', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 project:status_changed', { projectId: payload.projectId });

        const roles = await this.getRecipientRoles('project_status_changed');

        const { data: project } = await supabase
          .from('projects')
          .select(`
            organization_id,
            project_manager_id,
            client:clients (
              assigned_to,
              sales_rep_id
            )
          `)
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        const client = project.client as { assigned_to?: string; sales_rep_id?: string } | null;
        const recipients = await this.resolveRecipientsByRoles(roles, {
          projectId: payload.projectId,
          projectManagerId: project.project_manager_id,
          assigneeId: project.project_manager_id,
          salesRepId: client?.sales_rep_id,
          clientOwnerId: client?.assigned_to,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const statusLabels: Record<string, string> = {
          planning: 'In pianificazione',
          active: 'Attivo',
          on_hold: 'In pausa',
          completed: 'Completato',
          cancelled: 'Annullato',
        };

        await this.insertNotifications({
          eventType: 'project_status_changed',
          organizationId: project.organization_id,
          recipients,
          data: {
            title: 'Stato progetto cambiato',
            message: `${payload.projectName} è passato a: ${statusLabels[payload.toStatus] || payload.toStatus}`,
            entityType: 'project',
            entityId: payload.projectId,
            link: `/projects/${payload.projectId}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: payload.toStatus === 'completed' ? 'high' : 'normal',
            metadata: {
              from_status: payload.fromStatus,
              to_status: payload.toStatus,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in project:status_changed:', error);
      }
    });

    // Project Status Update → Notifica in base ai ruoli configurati
    eventBus.onEvent('project:status_update', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 project:status_update', { projectId: payload.projectId, isEdit: payload.isEdit });

        const roles = await this.getRecipientRoles('project_status_update');

        const { data: project } = await supabase
          .from('projects')
          .select(`
            organization_id,
            project_manager_id,
            client:clients (
              assigned_to,
              sales_rep_id
            )
          `)
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        const client = project.client as { assigned_to?: string; sales_rep_id?: string } | null;
        const recipients = await this.resolveRecipientsByRoles(roles, {
          projectId: payload.projectId,
          projectManagerId: project.project_manager_id,
          assigneeId: project.project_manager_id,
          salesRepId: client?.sales_rep_id,
          clientOwnerId: client?.assigned_to,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const healthLabels: Record<string, string> = {
          on_track: 'In linea',
          at_risk: 'A rischio',
          delayed: 'In ritardo',
        };

        const title = payload.isEdit ? 'Aggiornamento progetto modificato' : 'Nuovo aggiornamento progetto';
        const healthLabel = healthLabels[payload.healthStatus] || payload.healthStatus;
        const message = `${this.currentUserName || 'Qualcuno'} ha ${payload.isEdit ? 'modificato' : 'pubblicato'} un aggiornamento per ${payload.projectName} — Stato: ${healthLabel}`;

        await this.insertNotifications({
          eventType: 'project_status_update',
          organizationId: project.organization_id,
          recipients,
          data: {
            title,
            message,
            entityType: 'project',
            entityId: payload.projectId,
            link: `/projects/${payload.projectId}?tab=brief`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: payload.healthStatus === 'delayed' ? 'high' : 'normal',
            metadata: {
              health_status: payload.healthStatus,
              is_edit: payload.isEdit,
              status_update_id: payload.statusUpdateId,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in project:status_update:', error);
      }
    });

    // Project Comment → Notifica in base ai ruoli configurati
    eventBus.onEvent('project:comment', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 project:comment', { projectId: payload.projectId, taskId: payload.taskId });

        const eventType = payload.taskId ? 'task_comment' : 'project_comment';
        const roles = await this.getRecipientRoles(eventType);

        const { data: project } = await supabase
          .from('projects')
          .select('organization_id, project_manager_id')
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        // Nota: 'mentioned_users' sono gestiti separatamente da MentionNotificationService
        const recipients = await this.resolveRecipientsByRoles(roles, {
          projectId: payload.projectId,
          projectManagerId: project.project_manager_id,
          taskAssigneeId: payload.taskAssigneeId,
          taskCreatorId: payload.taskCreatorId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        // Tronca contenuto commento per preview
        const commentPreview = payload.commentContent
          ? payload.commentContent.substring(0, 100) + (payload.commentContent.length > 100 ? '...' : '')
          : '';

        // Costruisci messaggio in base a se è su una task specifica
        const taskInfo = payload.taskTitle ? ` sulla task "${payload.taskTitle}"` : '';

        await this.insertNotifications({
          eventType,
          organizationId: project.organization_id,
          recipients,
          data: {
            title: payload.taskId ? 'Nuovo commento su task' : 'Nuovo commento',
            message: `${this.currentUserName || 'Qualcuno'} ha commentato${taskInfo} nel progetto "${payload.projectName}"${commentPreview ? `: ${commentPreview}` : ''}`,
            entityType: payload.taskId ? 'task' : 'project',
            entityId: payload.taskId || payload.projectId,
            link: `/projects/${payload.projectId}${payload.taskId ? `?task=${payload.taskId}` : ''}`,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              task_id: payload.taskId,
              task_title: payload.taskTitle,
              comment_id: payload.commentId,
              // Attiva l'arricchimento email server-side (create-notification):
              // descrizione task, stato task/progetto, thread commenti.
              // Solo per i commenti su task (non sui commenti di progetto).
              email_task_thread: Boolean(payload.taskId),
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in project:comment:', error);
      }
    });
  }

  // ============================================================
  // TASK EVENT HANDLERS
  // ============================================================

  private registerTaskEvents() {
    // Task Assigned → Notifica in base ai ruoli configurati
    eventBus.onEvent('task:assigned', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 task:assigned', { taskId: payload.taskId });

        const { data: project } = await supabase
          .from('projects')
          .select('organization_id, project_manager_id')
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        const roles = await this.getRecipientRoles('task_assigned');
        const recipients = await this.resolveRecipientsByRoles(roles, {
          taskAssigneeId: payload.assignedToUserId,
          taskCreatorId: payload.createdByUserId,
          projectId: payload.projectId,
          projectManagerId: project.project_manager_id,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        // Assegnazione in blocco (sezione): una sola notifica riassuntiva
        const isBulk = (payload.taskCount ?? 1) > 1;
        const taskLink = isBulk
          ? `/projects/${payload.projectId}`
          : `/projects/${payload.projectId}?task=${payload.taskId}`;

        await this.insertNotifications({
          eventType: 'task_assigned',
          organizationId: project.organization_id,
          recipients,
          data: {
            title: isBulk ? 'Nuove task assegnate' : 'Nuova task assegnata',
            message: isBulk
              ? buildBulkNotificationMessage(this.currentUserName || 'Qualcuno', payload.taskCount!, payload.sectionName, payload.projectName)
              : `${this.currentUserName || 'Qualcuno'} ti ha assegnato la task "${payload.taskTitle}"${payload.projectName ? ` nel progetto "${payload.projectName}"` : ''}`,
            entityType: 'task',
            entityId: payload.taskId,
            link: taskLink,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: 'normal',
            metadata: {
              project_id: payload.projectId,
              project_name: payload.projectName,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in task:assigned:', error);
      }
    });

    // Task Status Changed → Notifica in base ai ruoli configurati
    eventBus.onEvent('task:status_changed', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 task:status_changed', { taskId: payload.taskId, toStatus: payload.toStatus });

        const eventType = 'task_status_changed';
        const roles = await this.getRecipientRoles(eventType);

        const { data: project } = await supabase
          .from('projects')
          .select('organization_id, project_manager_id')
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        const recipients = await this.resolveRecipientsByRoles(roles, {
          projectId: payload.projectId,
          projectManagerId: project.project_manager_id,
          taskAssigneeId: payload.assignedToUserId,
          taskCreatorId: payload.createdByUserId,
          excludeUserId: payload.changedByUserId,
        });

        if (recipients.length === 0) return;

        // Mappa stati in italiano
        const statusMap: Record<string, string> = {
          'todo': 'Da Fare',
          'in_progress': 'In Corso',
          'review': 'In Revisione',
          'completed': 'Completata',
          'standby': 'In Attesa',
        };

        const newStatusLabel = statusMap[payload.toStatus] || payload.toStatus;
        const isCompleted = payload.toStatus === 'completed';

        const taskLink = `/projects/${payload.projectId}?task=${payload.taskId}`;

        await this.insertNotifications({
          eventType,
          organizationId: project.organization_id,
          recipients,
          data: {
            title: isCompleted ? 'Task completata' : 'Stato task modificato',
            message: isCompleted
              ? `${this.currentUserName || 'Qualcuno'} ha completato la task "${payload.taskTitle}"${payload.projectName ? ` nel progetto "${payload.projectName}"` : ''}`
              : `${this.currentUserName || 'Qualcuno'} ha cambiato lo stato della task "${payload.taskTitle}" in "${newStatusLabel}"${payload.projectName ? ` nel progetto "${payload.projectName}"` : ''}`,
            entityType: 'task',
            entityId: payload.taskId,
            link: taskLink,
            senderId: this.currentUserId || undefined,
            senderName: this.currentUserName || undefined,
            priority: isCompleted ? 'high' : 'normal',
            metadata: {
              project_id: payload.projectId,
              project_name: payload.projectName,
              from_status: payload.fromStatus,
              to_status: payload.toStatus,
            }
          }
        });

        // Se completata, verifica se notificare i clienti nel portale (Fase 6)
        if (isCompleted) {
          this.notifyClientPortalTaskCompleted(payload, project.organization_id);
        }

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in task:status_changed:', error);
      }
    });

    // Client Comment → Notifica PM + assegnatario
    // In-app SEMPRE immediata, email debounced 30s se TaskDrawer è aperta sulla task
    eventBus.onEvent('task:client_comment', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 task:client_comment', { taskId: payload.taskId });

        if (this.activeTaskDrawerTaskId === payload.taskId) {
          // In-app immediata (skippa email), email debounced 30s
          await this.handleClientCommentNotification(payload, { skipAsyncDelivery: true });

          // Clear timer esistente per questa task
          const existing = this.clientCommentDebounceTimers.get(payload.taskId);
          if (existing) clearTimeout(existing);

          // Set nuovo timer 30s solo per email/push
          this.clientCommentDebounceTimers.set(
            payload.taskId,
            setTimeout(() => {
              this.triggerClientCommentAsyncDelivery(payload);
              this.clientCommentDebounceTimers.delete(payload.taskId);
            }, 30000)
          );
          console.log('[NotificationTriggerService] In-app sent, email debounced 30s (TaskDrawer open)');
          return;
        }

        // Task drawer non aperta → tutto immediato (in-app + email)
        await this.handleClientCommentNotification(payload);

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in task:client_comment:', error);
      }
    });

    // Collaborator Comment → Notifica PM + assegnatario + creatore + membri progetto
    // In-app only: l'email viene inviata dall'edge function chiamata dal portale collaboratore
    eventBus.onEvent('task:collaborator_comment', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 task:collaborator_comment', { taskId: payload.taskId });

        const { data: project } = await supabase
          .from('projects')
          .select('organization_id, project_manager_id')
          .eq('id', payload.projectId)
          .single();

        if (!project?.organization_id) return;

        const roles = await this.getRecipientRoles('task_collaborator_comment');

        const recipients = await this.resolveRecipientsByRoles(roles, {
          projectId: payload.projectId,
          projectManagerId: payload.projectManagerId || project.project_manager_id,
          taskAssigneeId: payload.taskAssigneeId,
          taskCreatorId: payload.taskCreatorId,
          excludeUserId: this.currentUserId || undefined,
        });

        if (recipients.length === 0) return;

        const commentPreview = payload.commentContent
          ? payload.commentContent.replace(/^\[Collaboratore: .+?\]\n?/, '').substring(0, 100)
          : '';

        const taskLink = `/projects/${payload.projectId}?task=${payload.taskId}`;

        // skipAsyncDelivery: true → evita email duplicate (l'edge function dal portale la invia già)
        await this.insertNotifications({
          eventType: 'task_collaborator_comment',
          organizationId: project.organization_id,
          recipients,
          data: {
            title: 'Messaggio dal collaboratore',
            message: `${payload.collaboratorName} ha commentato sulla task "${payload.taskTitle}"${payload.projectName ? ` nel progetto "${payload.projectName}"` : ''}${commentPreview ? `: ${commentPreview}` : ''}`,
            entityType: 'task',
            entityId: payload.taskId,
            link: taskLink,
            senderName: payload.collaboratorName,
            priority: 'high',
            metadata: {
              project_id: payload.projectId,
              project_name: payload.projectName,
              collaborator_name: payload.collaboratorName,
            }
          }
        }, { skipAsyncDelivery: true });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in task:collaborator_comment:', error);
      }
    });
  }

  /**
   * Handler effettivo per la notifica messaggio cliente
   */
  private async handleClientCommentNotification(payload: {
    projectId: string;
    taskId: string;
    taskTitle: string;
    commentContent: string;
    clientName: string;
    clientAccessId?: string;
    projectManagerId?: string;
    taskAssigneeId?: string;
    projectName: string;
  }, options?: { skipAsyncDelivery?: boolean }): Promise<void> {
    try {
      // Recupera organization_id e PM
      const { data: project } = await supabase
        .from('projects')
        .select('organization_id, project_manager_id')
        .eq('id', payload.projectId)
        .single();

      if (!project?.organization_id) return;

      // Recupera ruoli configurati per task_client_comment
      const roles = await this.getRecipientRoles('task_client_comment');

      // Fetch task creator per il context
      let taskCreatorId: string | undefined;
      if (roles.includes('task_creator')) {
        const { data: task } = await supabase
          .from('tasks')
          .select('created_by')
          .eq('id', payload.taskId)
          .single();
        taskCreatorId = task?.created_by || undefined;
      }

      const recipients = await this.resolveRecipientsByRoles(roles, {
        projectId: payload.projectId,
        projectManagerId: payload.projectManagerId || project.project_manager_id,
        taskAssigneeId: payload.taskAssigneeId,
        taskCreatorId,
      });

      // Escludi l'utente CRM se corrisponde al client del portale (match per email)
      const recipientSet = new Set(recipients);
      if (this.currentUserId && this.currentUserEmail && payload.clientAccessId) {
        try {
          const { data: accessData } = await supabase
            .from('client_portal_access')
            .select('client:clients!inner(email)')
            .eq('id', payload.clientAccessId)
            .single();

          const clientEmail = (accessData?.client as any)?.email;
          if (clientEmail && clientEmail.toLowerCase() === this.currentUserEmail.toLowerCase()) {
            recipientSet.delete(this.currentUserId);
          }
        } catch {
          // Non-blocking: se il check fallisce, procedi normalmente
        }
      }

      if (recipientSet.size === 0) return;

      // Tronca preview commento
      const commentPreview = payload.commentContent
        ? payload.commentContent.substring(0, 100) + (payload.commentContent.length > 100 ? '...' : '')
        : '';

      const taskLink = `/projects/${payload.projectId}?task=${payload.taskId}`;

      await this.insertNotifications({
        eventType: 'task_client_comment',
        organizationId: project.organization_id,
        recipients: Array.from(recipientSet),
        data: {
          title: 'Messaggio dal cliente',
          message: `${payload.clientName} ha scritto sulla task "${payload.taskTitle}"${payload.projectName ? ` nel progetto "${payload.projectName}"` : ''}${commentPreview ? `: ${commentPreview}` : ''}`,
          entityType: 'task',
          entityId: payload.taskId,
          link: taskLink,
          senderId: payload.clientAccessId || undefined,
          senderName: payload.clientName,
          priority: 'high',
          metadata: {
            project_id: payload.projectId,
            project_name: payload.projectName,
            client_name: payload.clientName,
          }
        }
      }, options);

    } catch (error) {
      console.error('[NotificationTriggerService] ❌ Error in handleClientCommentNotification:', error);
    }
  }

  /**
   * Trigger solo email/push per messaggio cliente (usato dopo debounce 30s)
   */
  private async triggerClientCommentAsyncDelivery(payload: {
    projectId: string;
    taskId: string;
    taskTitle: string;
    commentContent: string;
    clientName: string;
    projectManagerId?: string;
    taskAssigneeId?: string;
    projectName: string;
  }): Promise<void> {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('organization_id, project_manager_id')
        .eq('id', payload.projectId)
        .single();

      if (!project?.organization_id) return;

      const roles = await this.getRecipientRoles('task_client_comment');

      // Fetch task creator per coerenza con handleClientCommentNotification
      let taskCreatorId: string | undefined;
      if (roles.includes('task_creator')) {
        const { data: task } = await supabase
          .from('tasks')
          .select('created_by')
          .eq('id', payload.taskId)
          .single();
        taskCreatorId = task?.created_by || undefined;
      }

      const recipients = await this.resolveRecipientsByRoles(roles, {
        projectId: payload.projectId,
        projectManagerId: payload.projectManagerId || project.project_manager_id,
        taskAssigneeId: payload.taskAssigneeId,
        taskCreatorId,
        excludeUserId: this.currentUserId || undefined,
      });

      if (recipients.length === 0) return;

      const commentPreview = payload.commentContent
        ? payload.commentContent.substring(0, 100) + (payload.commentContent.length > 100 ? '...' : '')
        : '';

      const taskLink = `/projects/${payload.projectId}?task=${payload.taskId}`;

      this.triggerAsyncDelivery({
        eventType: 'task_client_comment',
        organizationId: project.organization_id,
        recipients,
        data: {
          title: 'Messaggio dal cliente',
          message: `${payload.clientName} ha scritto sulla task "${payload.taskTitle}"${payload.projectName ? ` nel progetto "${payload.projectName}"` : ''}${commentPreview ? `: ${commentPreview}` : ''}`,
          entityType: 'task',
          entityId: payload.taskId,
          link: taskLink,
          priority: 'high',
          metadata: {
            project_id: payload.projectId,
            project_name: payload.projectName,
            client_name: payload.clientName,
          }
        }
      });
    } catch (error) {
      console.error('[NotificationTriggerService] ❌ Error in triggerClientCommentAsyncDelivery:', error);
    }
  }

  /**
   * Notifica i clienti nel portale quando una task visibile viene completata
   * Fire-and-forget: non blocca il flusso principale
   */
  private async notifyClientPortalTaskCompleted(payload: {
    projectId: string;
    taskId: string;
    taskTitle: string;
    taskSection?: string;
    projectName: string;
  }, organizationId: string): Promise<void> {
    try {
      // Verifica se ci sono client portal access collegati al progetto
      const { data: portalAccess } = await supabase
        .from('client_portal_projects')
        .select('client_access_id, shared_sections')
        .eq('project_id', payload.projectId);

      if (!portalAccess || portalAccess.length === 0) return;

      for (const access of portalAccess) {
        // Verifica che la sezione della task sia visibile al client
        const sharedSections = access.shared_sections as string[] | null;

        // Se shared_sections è null/vuoto: nessuna sezione condivisa, skip
        // Se task ha una sezione: deve essere nelle shared_sections
        // Se task non ha sezione: visibile solo se non ci sono shared_sections
        let isVisible = false;
        if (payload.taskSection && sharedSections && sharedSections.length > 0) {
          isVisible = sharedSections.includes(payload.taskSection);
        } else if (!payload.taskSection && (!sharedSections || sharedSections.length === 0)) {
          isVisible = true;
        }

        if (!isVisible) continue;

        // Verifica che il client_access sia attivo
        const { data: clientAccess } = await supabase
          .from('client_portal_access')
          .select('id, is_active')
          .eq('id', access.client_access_id)
          .eq('is_active', true)
          .single();

        if (!clientAccess) continue;

        // Fire-and-forget: chiama edge function per notifica cliente
        supabase.functions.invoke('create-client-notification', {
          body: {
            organization_id: organizationId,
            client_access_id: access.client_access_id,
            event_type: 'task_completed',
            data: {
              title: 'Task completata',
              message: `La task "${payload.taskTitle}" è stata completata`,
              link: `/projects/${payload.projectId}?task=${payload.taskId}`,
              task_id: payload.taskId,
              project_id: payload.projectId,
            },
            send_email: true,
          }
        }).catch(err => {
          console.error('[NotificationTriggerService] Error notifying client portal:', err);
        });
      }

    } catch (error) {
      console.error('[NotificationTriggerService] ❌ Error in notifyClientPortalTaskCompleted:', error);
    }
  }

  // ============================================================
  // PARTNER TASK EVENT HANDLERS
  // ============================================================

  private registerPartnerTaskEvents() {
    // Partner Task Comment → Notifica in base ai ruoli configurati
    eventBus.onEvent('partner_task:comment', async (payload) => {
      try {
        console.log('[NotificationTriggerService] 📨 partner_task:comment', { taskId: payload.taskId });

        const roles = await this.getRecipientRoles('partner_task_comment');

        // Fetch previous commenters per il ruolo 'previous_commenters'
        let previousCommenterIds: string[] = [];
        if (roles.includes('previous_commenters')) {
          const { data: previousComments } = await supabase
            .from('partner_task_comments')
            .select('created_by')
            .eq('task_id', payload.taskId);
          previousCommenterIds = (previousComments || [])
            .map(c => c.created_by)
            .filter((id): id is string => !!id);
        }

        const recipients = await this.resolveRecipientsByRoles(roles, {
          taskCreatorId: payload.assignedById,
          assigneeId: payload.assignedById,
          previousCommenterIds,
          excludeUserId: payload.commenterId,
        });

        if (recipients.length === 0) return;

        const commentPreview = payload.commentContent
          ? payload.commentContent.substring(0, 100) + (payload.commentContent.length > 100 ? '...' : '')
          : '';

        await this.insertNotifications({
          eventType: 'partner_task_comment',
          organizationId: payload.organizationId,
          recipients,
          data: {
            title: 'Nuovo commento su task partner',
            message: `${payload.commenterName} ha commentato sul task "${payload.taskTitle}"${payload.partnerName ? ` (Partner: ${payload.partnerName})` : ''}${commentPreview ? `: ${commentPreview}` : ''}`,
            entityType: 'partner_task',
            entityId: payload.taskId,
            link: `/partners?task=${payload.taskId}`,
            senderId: payload.commenterId,
            senderName: payload.commenterName,
            priority: 'normal',
            metadata: {
              partner_id: payload.partnerId,
              partner_name: payload.partnerName,
            }
          }
        });

      } catch (error) {
        console.error('[NotificationTriggerService] ❌ Error in partner_task:comment:', error);
      }
    });
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  /**
   * Risolve i ruoli configurati in user ID concreti.
   * Metodo centralizzato usato da tutti gli handler.
   */
  private async resolveRecipientsByRoles(
    roles: string[],
    context: {
      assigneeId?: string;
      creatorId?: string;
      projectId?: string;
      projectManagerId?: string;
      taskAssigneeId?: string;
      taskCreatorId?: string;
      entityType?: string;
      entityId?: string;
      salesRepId?: string;
      clientOwnerId?: string;
      mentionedUserIds?: string[];
      previousCommenterIds?: string[];
      excludeUserId?: string;
    }
  ): Promise<string[]> {
    const recipientSet = new Set<string>();

    // assignee — responsabile generico (lead, deal, client, project)
    if (roles.includes('assignee') && context.assigneeId) {
      recipientSet.add(context.assigneeId);
    }

    // creator — creatore dell'entità
    if (roles.includes('creator') && context.creatorId) {
      recipientSet.add(context.creatorId);
    }

    // pm — project manager
    if (roles.includes('pm') && context.projectManagerId) {
      recipientSet.add(context.projectManagerId);
    }

    // task_assignee
    if (roles.includes('task_assignee') && context.taskAssigneeId) {
      recipientSet.add(context.taskAssigneeId);
    }

    // task_creator
    if (roles.includes('task_creator') && context.taskCreatorId) {
      recipientSet.add(context.taskCreatorId);
    }

    // project_members — tutti gli assegnati al progetto
    if (roles.includes('project_members') && context.projectId) {
      const { data: assignments } = await supabase
        .from('project_assignments')
        .select('user_id')
        .eq('project_id', context.projectId);
      assignments?.forEach(a => recipientSet.add(a.user_id));
    }

    // entity_followers — utenti che seguono l'entità (via RPC)
    if (roles.includes('entity_followers') && context.entityType && context.entityId) {
      const { data: followers } = await supabase
        .rpc('get_notification_recipients', {
          p_entity_type: context.entityType,
          p_entity_id: context.entityId,
          p_event_type: '',
          p_exclude_user_id: context.excludeUserId || null
        });
      followers?.forEach((f: { recipient_id: string }) => recipientSet.add(f.recipient_id));
    }

    // sales_rep — commerciale
    if (roles.includes('sales_rep') && context.salesRepId) {
      recipientSet.add(context.salesRepId);
    }

    // client_owner — responsabile cliente
    if (roles.includes('client_owner') && context.clientOwnerId) {
      recipientSet.add(context.clientOwnerId);
    }

    // mentioned_users
    if (roles.includes('mentioned_users') && context.mentionedUserIds) {
      context.mentionedUserIds.forEach(id => recipientSet.add(id));
    }

    // previous_commenters — chi ha già commentato
    if (roles.includes('previous_commenters') && context.previousCommenterIds) {
      context.previousCommenterIds.forEach(id => recipientSet.add(id));
    }

    // Escludi chi ha fatto l'azione
    if (context.excludeUserId) {
      recipientSet.delete(context.excludeUserId);
    }

    return Array.from(recipientSet);
  }

  /**
   * Lead o Cliente: clients non ha una colonna is_lead, lo dice lo stato
   * (clients.stato_cliente = client_states.id). I valori vecchi sono testo libero.
   */
  private async isLeadState(statoCliente: string | null | undefined): Promise<boolean> {
    if (!statoCliente) return false;
    // Valori vecchi in testo libero ('lead', 'cliente'): niente richiesta, darebbe errore 400 (id e' uuid)
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(statoCliente)) {
      return ['lead', 'prospect'].includes(statoCliente.toLowerCase());
    }
    const { data } = await supabase
      .from('client_states')
      .select('is_lead')
      .eq('id', statoCliente)
      .maybeSingle();
    if (data) return data.is_lead === true;
    return ['lead', 'prospect'].includes(statoCliente.toLowerCase());
  }

  /**
   * Recupera i ruoli configurati per ricevere notifiche per un evento
   * Cached per evitare query ripetute
   * Prima i destinatari scelti dall'organizzazione (organization_notification_defaults.recipient_roles),
   * se non li ha scelti quelli globali di notification_event_types (come prima).
   */
  private async getRecipientRoles(eventType: string): Promise<string[]> {
    // Check cache first
    if (this.recipientRolesCache.has(eventType)) {
      return this.recipientRolesCache.get(eventType)!;
    }

    try {
      if (this.currentOrganizationId) {
        const { data: orgDefault } = await supabase
          .from('organization_notification_defaults')
          .select('recipient_roles')
          .eq('organization_id', this.currentOrganizationId)
          .eq('event_type', eventType)
          .maybeSingle();

        if (orgDefault?.recipient_roles && orgDefault.recipient_roles.length > 0) {
          this.recipientRolesCache.set(eventType, orgDefault.recipient_roles);
          return orgDefault.recipient_roles;
        }
      }

      const { data, error } = await supabase
        .from('notification_event_types')
        .select('recipient_roles')
        .eq('id', eventType)
        .single();

      if (error || !data?.recipient_roles) {
        // Default: notifica tutti (PM + membri progetto + menzionati)
        const defaultRoles = ['pm', 'project_members', 'mentioned_users'];
        this.recipientRolesCache.set(eventType, defaultRoles);
        return defaultRoles;
      }

      this.recipientRolesCache.set(eventType, data.recipient_roles);
      return data.recipient_roles;
    } catch (error) {
      console.error('[NotificationTriggerService] Error fetching recipient roles:', error);
      return ['pm', 'project_members', 'mentioned_users'];
    }
  }

  /**
   * Invalida la cache dei ruoli (chiamare quando admin modifica le impostazioni)
   */
  invalidateRecipientRolesCache(): void {
    this.recipientRolesCache.clear();
  }

  /**
   * INSERT diretto in notifications - VELOCE per UI
   * La realtime subscription aggiornerà automaticamente lo store
   */
  private async insertNotifications(params: InsertNotificationParams, options?: { skipAsyncDelivery?: boolean }): Promise<void> {
    const { eventType, organizationId, recipients, data } = params;

    if (recipients.length === 0) {
      console.log('[NotificationTriggerService] No recipients, skipping');
      return;
    }

    const notifications = recipients.map(userId => ({
      user_id: userId,
      organization_id: organizationId,
      event_type: eventType,
      type: data.priority === 'urgent' ? 'error' :
            data.priority === 'high' ? 'warning' : 'info',
      title: data.title,
      message: data.message,
      link: data.link,
      entity_type: data.entityType,
      entity_id: data.entityId,
      sender_id: data.senderId,
      sender_name: data.senderName,
      priority: data.priority || 'normal',
      metadata: data.metadata || {},
      read: false,
      delivered_via: ['in_app']
    }));

    const { error } = await supabase
      .from('notifications')
      .insert(notifications);

    if (error) {
      console.error('[NotificationTriggerService] Insert error:', error);
    } else {
      console.log(`[NotificationTriggerService] ✅ Created ${notifications.length} notification(s) for ${eventType}`);

      // Emit notification-received per l'utente corrente (toast in-app immediato)
      // Bypass del realtime subscription che potrebbe non essere attivo
      const currentUserNotification = notifications.find(n => n.user_id === this.currentUserId);
      if (currentUserNotification) {
        try {
          window.dispatchEvent(new CustomEvent('notification-received', {
            detail: { notification: currentUserNotification }
          }));
        } catch (e) {
          // Ignore event dispatch errors
        }
      }
    }

    // Fire-and-forget: chiama edge function per email/push delivery
    // Non blocchiamo il flusso UI - l'insert diretto già gestisce in-app
    if (!options?.skipAsyncDelivery) {
      this.triggerAsyncDelivery(params);
    }
  }

  /**
   * Chiama create-notification edge function per email e push notifications
   * Fire-and-forget: non blocca il flusso principale
   */
  private triggerAsyncDelivery(params: InsertNotificationParams): void {
    const { eventType, organizationId, recipients, data } = params;

    // Non attendiamo la risposta - fire and forget
    supabase.functions.invoke('create-notification', {
      body: {
        organization_id: organizationId,
        event_type: eventType,
        recipients: recipients,
        skip_in_app: true, // Evita duplicati: INSERT diretto già fatto sopra
        data: {
          title: data.title,
          message: data.message,
          link: data.link,
          entity_type: data.entityType,
          entity_id: data.entityId,
          sender_id: data.senderId,
          sender_name: data.senderName,
          priority: data.priority,
          metadata: data.metadata
        }
      }
    }).then(({ error }) => {
      if (error) {
        console.error('[NotificationTriggerService] Async delivery error:', error);
      } else {
        console.log(`[NotificationTriggerService] 📨 Async delivery triggered for ${eventType}`);
      }
    }).catch(err => {
      console.error('[NotificationTriggerService] Async delivery failed:', err);
    });
  }
}

// Export singleton instance
export const notificationTriggerService = new NotificationTriggerService();
